import { useEffect, useState } from "react";
import { api } from "../../App";

// Productivity — throughput / on-time / capacity, all derived from the pipeline's
// own timestamps. No manual reporting. Reads /api/ptracker/productivity.

const mono = "var(--font-mono)";

function Tile({ label, value, sub, tone }) {
  const color = tone === "red" ? "var(--r)" : tone === "amber" ? "#d97706" : "var(--t1)";
  return (
    <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", padding: 16 }}>
      <div style={{ fontSize: 26, fontWeight: 800, color, letterSpacing: "-.02em", lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 600, color: "var(--t3)", letterSpacing: ".08em", textTransform: "uppercase", marginTop: 8 }}>{label}</div>
      {sub && <div style={{ fontFamily: mono, fontSize: 10, color: "var(--t4)", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", padding: 18 }}>
      <div style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: "var(--t3)", letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

export default function PtrackerProductivity() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/ptracker/productivity")
      .then((r) => { if (alive) setD(r.data); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || "Couldn't load productivity."); });
    return () => { alive = false; };
  }, []);

  const maxWip = Math.max(1, ...((d?.wip || []).map((w) => w.n)));
  const maxLoad = Math.max(1, ...((d?.team_load || []).map((r) => r.count)));

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1100, margin: "0 auto" }} data-testid="ptracker-productivity">
      <h1 style={{ fontFamily: "var(--font-heading, var(--font-orb))", fontSize: 30, fontWeight: 700, color: "var(--t1)", margin: 0, textTransform: "uppercase" }}>Productivity</h1>
      <p style={{ fontFamily: mono, fontSize: 12, color: "var(--t3)", letterSpacing: ".04em", marginTop: 4, marginBottom: 20 }}>
        Throughput, delivery and capacity — computed live from the pipeline. Nobody files a report.
      </p>

      {err && <div style={{ color: "var(--r)", fontFamily: mono, fontSize: 12 }}>{err}</div>}

      {d && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
            <Tile label="In flight" value={d.in_flight} />
            <Tile label="Delivered live" value={d.delivered} />
            <Tile label="On-time %" value={d.on_time_pct} tone={d.on_time_pct >= 70 ? null : "amber"} />
            <Tile label="Overdue" value={d.overdue} tone={d.overdue ? "red" : null} />
            <Tile label="KPIs missing 7d+" value={d.kpi_backlog} tone={d.kpi_backlog ? "amber" : null} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 12, marginTop: 12 }}>
            <Panel title="Work in progress by phase">
              {(d.wip || []).map((w) => (
                <div key={w.key} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontFamily: mono, fontSize: 11, color: "var(--t2)", marginBottom: 4 }}>
                    <span>{w.label}</span><span style={{ fontWeight: 700, color: "var(--t1)" }}>{w.n}</span>
                  </div>
                  <div style={{ height: 8, background: "var(--s2)", borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(w.n / maxWip) * 100}%`, background: "var(--r)", borderRadius: 6, transition: "width .6s" }} />
                  </div>
                </div>
              ))}
            </Panel>

            <Panel title={`Bottlenecks — stuck 5+ days (${(d.bottlenecks || []).length})`}>
              {(d.bottlenecks || []).length === 0 ? (
                <div style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)", textAlign: "center", padding: "14px 0" }}>Clean flow. ✨</div>
              ) : (d.bottlenecks || []).map((a) => (
                <div key={a.asset_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                  <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: "var(--r)", background: "var(--rd)", borderRadius: 3, padding: "1px 6px" }}>{a.stage_age_days}d</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--t1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.title}</span>
                  <span style={{ fontFamily: mono, fontSize: 9, color: "var(--t3)", textTransform: "uppercase" }}>{a.status}</span>
                </div>
              ))}
            </Panel>

            <Panel title="Whose plate is full — assets waiting per person">
              {(d.team_load || []).length === 0 ? (
                <div style={{ fontFamily: mono, fontSize: 11, color: "var(--t3)", textAlign: "center", padding: "14px 0" }}>No assigned work in flight.</div>
              ) : (d.team_load || []).map((r) => (
                <div key={r.user_id} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span style={{ width: 110, fontFamily: "var(--font-sans)", fontSize: 12, color: "var(--t2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
                  <div style={{ flex: 1, height: 8, background: "var(--s2)", borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(r.count / maxLoad) * 100}%`, background: r.count >= 4 ? "var(--r)" : "rgba(227,25,55,.55)", borderRadius: 6 }} />
                  </div>
                  <span style={{ width: 20, textAlign: "right", fontFamily: mono, fontSize: 12, fontWeight: 700, color: "var(--t1)" }}>{r.count}</span>
                  {r.oldest >= 5 && <span style={{ fontFamily: mono, fontSize: 9, color: "var(--r)" }}>{r.oldest}d!</span>}
                </div>
              ))}
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
